// Copyright (c) Docugami, Inc. All rights reserved.

import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights, ITelemetryItem } from "@microsoft/applicationinsights-web";
import { Config } from "config";
import { Logger, LoggerCategory } from "utils/Logger";
import { TelemetryEvent, TelemetryPageView } from "./TelemetryEvents";
import { ITelemetryProvider, TelemetryEventData, TelemetryInitializer } from "./TelemetryProvider";

export class AppInsightsPlugin {
    static _instance: ReactPlugin;

    static get instance(): ReactPlugin {
        if (!AppInsightsPlugin._instance) {
            AppInsightsPlugin._instance = new ReactPlugin();
        }

        return AppInsightsPlugin._instance;
    }
}

export class AppInsightsTelemetryProvider implements ITelemetryProvider {
    private readonly _appInsights: ApplicationInsights;

    get providerId(): string { return "appInsights" }
    
    constructor(connectionString: string) {
        this._appInsights = new ApplicationInsights({
            config: {
                connectionString,
                extensions: [AppInsightsPlugin.instance],
                disableFetchTracking: false,
                enableCorsCorrelation: true,
                enableRequestHeaderTracking: true,
                enableResponseHeaderTracking: true,
                correlationHeaderDomains: Config.appInsights.correlationHeaderDomains
            }
        });
        this._appInsights.loadAppInsights();
        Logger.LogInformation(LoggerCategory.Telemetry, `App Insights provider initialized.`);
    }

    trackNamedEvent(name: TelemetryEvent | string, data?: TelemetryEventData): void {
        this._appInsights.trackEvent({ name: name }, data);
    }

    trackPageView(name: TelemetryPageView | string, data?: TelemetryEventData): void {
        this._appInsights.trackPageView({ name: name, properties: data });
    }

    trackException(error: Error, data?: TelemetryEventData): void {
        this._appInsights.trackException({ exception: error, properties: data });
    }

    setUserContext(userId: string, accountId: string): void {
        this._appInsights.setAuthenticatedUserContext(userId, accountId);
    }

    clearUserContext(): void {
        this._appInsights.clearAuthenticatedUserContext();
    }

    addInitializer(initializer: TelemetryInitializer): void {
        this._appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
            let data = item.data;
            if (!data) {
                data = {};
                item.data = data;
            }

            const result = initializer(data);
            return result;
        });
    }
}
